import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

import { Zone } from '../models/zone.models';
import { LanguageStateService } from './language-state.service';
import { ZoneConfigService } from './zone-config.service';

const STORAGE_KEY = 'active_zone_id';

@Injectable({ providedIn: 'root' })
export class ZoneStateService {
  readonly #document = inject(DOCUMENT);
  readonly #languageState = inject(LanguageStateService);
  readonly #zoneConfig = inject(ZoneConfigService);

  readonly #zones = signal<Zone[]>([]);
  readonly #activeZone = signal<Zone | null>(null);

  readonly zones = this.#zones.asReadonly();
  readonly activeZone = this.#activeZone.asReadonly();
  readonly requiresSelection = computed(() => this.#zoneConfig.enabled && this.#zones().length > 1);
  readonly activeZoneName = computed(() => {
    const zone = this.#activeZone();
    return zone ? this.resolveZoneName(zone) : '';
  });

  setZones(zones: Zone[]): void {
    if (!this.#zoneConfig.enabled) {
      this.#zones.set([]);
      this.#activeZone.set(null);
      this.#storage()?.removeItem(STORAGE_KEY);
      return;
    }

    this.#zones.set(zones);

    if (!zones.length) {
      this.#activeZone.set(null);
      this.#storage()?.removeItem(STORAGE_KEY);
      return;
    }

    if (zones.length === 1) {
      this.#activeZone.set(zones[0]);
      this.#storage()?.setItem(STORAGE_KEY, String(zones[0].id));
      return;
    }

    const storedId = this.#storage()?.getItem(STORAGE_KEY) ?? null;
    const restored = storedId ? (zones.find((z) => z.id === Number(storedId)) ?? null) : null;
    const activeZone = restored ?? zones[0];
    this.#activeZone.set(activeZone);
    this.#storage()?.setItem(STORAGE_KEY, String(activeZone.id));
  }

  setActiveZone(zone: Zone): void {
    if (!this.#zoneConfig.enabled) {
      return;
    }

    const nextZone = this.#zones().find(({ id }) => id === zone.id);

    if (!nextZone) {
      return;
    }

    this.#activeZone.set(nextZone);
    this.#storage()?.setItem(STORAGE_KEY, String(nextZone.id));
  }

  clear(): void {
    this.#zones.set([]);
    this.#activeZone.set(null);
    this.#storage()?.removeItem(STORAGE_KEY);
  }

  resolveZoneName(zone: Zone): string {
    const code = this.#languageState.activeCode();

    return zone.name[code] ?? Object.values(zone.name)[0] ?? `#${zone.id}`;
  }

  #storage(): Storage | null {
    return this.#document.defaultView?.sessionStorage ?? null;
  }
}
